import React, { useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import classNames from 'classnames';
import * as yup from 'yup';

import { ActionButtons, Flex } from '@hcs/design-system';
import { FormFieldsInput, FormFieldsSwitch, InputLayout } from '@hcs/forms';
import {
  OrganizationapiCreateOrgRequestInt,
  OrganizationResponseInt,
} from '@hcs/types';

import { useCreateOrganization } from '../../hooks/useCreateOrganization';
import { OrgTypeDropdown } from '../OrgTypeDropdown';

import styles from './CreateOrganization.module.css';
const FORM_SCHEMA = yup.object({
  name: yup.string().required('Name is required'),
  m3terAccountId: yup.string(),
  active: yup.boolean(),
  enterprise: yup.boolean(),
  test: yup.boolean(),
});
const MULTI_INPUT_THEME = { InputContainer: styles.MultiInputLine };
const dataHcName = 'create-org-form';

interface Props {
  defaultValues?: Partial<OrganizationapiCreateOrgRequestInt>;
  onCancel: VoidFunction;
  onSuccess: (newOrg: OrganizationResponseInt) => void;
  actionPortalIdRender?: string;
  className?: string;
}
export const CreateOrganizationForm = ({
  defaultValues,
  onCancel,
  className,
  onSuccess,
  actionPortalIdRender,
}: Props) => {
  const [hasAckWarning, setHasAckWarning] = useState(false);
  const { mutate: createOrganization } = useCreateOrganization({ onSuccess });
  const form = useForm<OrganizationapiCreateOrgRequestInt>({
    resolver: yupResolver(FORM_SCHEMA),
    defaultValues: {
      ...defaultValues,
      active: defaultValues?.active !== undefined ? defaultValues.active : true,
    },
  });
  const handleSubmit = (formData: OrganizationapiCreateOrgRequestInt) => {
    createOrganization(formData);
  };
  if (!hasAckWarning) {
    return (
      <Flex
        className={styles.Warning}
        direction="column"
        alignItems="stretch"
        fullWidth
      >
        <Flex className={styles.WarningTitle} fullWidth justifyContent="center">
          Warning - Potential Revenue Impact!
        </Flex>
        <div>
          This form should only be used for creating Organizations that{' '}
          <b>will NOT be billed for usage</b>. Any usage from an organization
          created this way will not be sent to the billing system.
        </div>
        <div>
          <b>Example Use Cases for this Form:</b>
          <ul>
            <li>Partner Accounts</li>
            <li>Internal Accounts</li>
            <li>Internal Test Accounts</li>
          </ul>
        </div>

        <div>
          <b>When NOT to use this Form:</b>
          <ul>
            <li>
              <b>OM Demo Accounts</b> - Granting access to OM Demo also grants
              access to the same products in OM <b>PROD</b>. These accounts
              should be created via the enterprise Salesforce Quoting process so
              any usage in the production environment is billed for.
            </li>
            <li>
              <b>Self Serve Demo Accounts</b> - The Platform will make requests
              to m3ter for plan information and will change the in-app
              experience accordingly. Organizations created with this form will
              not see the self serve experience.
            </li>
          </ul>
        </div>

        <ActionButtons
          actions={[
            {
              label: 'Go Back',
              primary: true,
              dataHcName: 'create-org-back',
              onClick: onCancel,
            },
            {
              label: 'Create Free Org',
              secondary: true,
              dataHcName: 'create-org-ack',
              onClick: () => {
                setHasAckWarning(true);
              },
            },
          ]}
        />
      </Flex>
    );
  }
  return (
    <FormProvider {...form}>
      <form
        data-hc-name={`${dataHcName}-form`}
        className={classNames(styles.Form, className)}
      >
        <InputLayout label="Name" required>
          <FormFieldsInput
            name="name"
            label=""
            error={form.formState.errors.name?.message?.toString()}
            className={styles.Input}
          />
        </InputLayout>
        <InputLayout
          label="Org Type"
          theme={MULTI_INPUT_THEME}
          required={false}
        >
          <Controller
            name="type"
            control={form.control}
            render={({ field }) => {
              return (
                <OrgTypeDropdown
                  {...field}
                  className={styles.Input}
                  value={field.value || null}
                  onSelect={(val) => {
                    field.onChange(val);
                    form.clearErrors('type');
                  }}
                  error={form.formState.errors.type?.message?.toString()}
                />
              );
            }}
          />
        </InputLayout>
        <InputLayout label="" theme={MULTI_INPUT_THEME} required={false}>
          <FormFieldsSwitch name="active" label="Active" />
          <FormFieldsSwitch name="test" label="Test Account" />
        </InputLayout>
        <ActionButtons
          dataHcName={`${dataHcName}-actions`}
          actions={[
            {
              dataHcName: `${dataHcName}-cancel`,
              label: 'Cancel',
              secondary: true,
              onClick: onCancel,
            },
            {
              dataHcName: `${dataHcName}-submit`,
              label: 'Create Free Org',
              primary: true,
              onClick: form.handleSubmit(handleSubmit),
            },
          ]}
          portalIdRender={actionPortalIdRender}
        />
      </form>
    </FormProvider>
  );
};
